import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { EarningApiService } from '../../shared/services/earning-api.service';
import { WithdrawalApiService } from '../../shared/services/withdrawal-api.service';
import { BankAccountApiService } from '../../shared/services/bank-account-api.service';
import { InstructorPlanApiService, PlanAssignment, InstructorPlan } from '../../shared/services/instructor-plan-api.service';
import { TenantService, TenantInfo } from '../../shared/services/tenant.service';
import { User } from '../../shared/core/state/users/models/user.model';
import { selectUser } from '../../shared/core/state/users/user.selectors';
import {
  EarningLedger,
  Earning,
} from '../../shared/core/state/earnings/earning.model';
import { WithdrawalRequest } from '../../shared/core/state/earnings/withdrawal.model';
import { BankAccount } from '../../shared/core/state/earnings/bank-account.model';

@Component({
  selector: 'black-earnings',
  templateUrl: './earnings.component.html',
  styleUrls: ['./earnings.component.css'],
  standalone: false,
})
export class EarningsComponent implements OnInit, OnDestroy {
  private earningService = inject(EarningApiService);
  private withdrawalService = inject(WithdrawalApiService);
  private bankAccountService = inject(BankAccountApiService);
  private planService = inject(InstructorPlanApiService);
  private tenantService = inject(TenantService);
  private store = inject(Store);

  private subscription = new Subscription();

  currentTenant = signal<TenantInfo | null>(null);
  user = signal<Partial<User> | null>(null);

  isSoloTutor = computed(() => this.currentTenant()?.type === 'SOLO_INSTRUCTOR');
  isAcademyAdmin = computed(() => {
    return this.currentTenant()?.type === 'ACADEMY' && this.user()?.role === 'ADMINISTRATOR';
  });

  ledger = signal<EarningLedger | null>(null);
  earnings = signal<Earning[]>([]);
  withdrawals = signal<WithdrawalRequest[]>([]);
  bankAccount = signal<BankAccount | null>(null);
  activePlanAssignment = signal<PlanAssignment | null>(null);

  totalEarningsCount = signal(0);
  totalWithdrawalsCount = signal(0);

  /** Resolved plan object from the active assignment. */
  activePlan = computed<InstructorPlan | null>(() => {
    const assignment = this.activePlanAssignment();
    if (!assignment) return null;
    const plan = assignment.plan;
    return typeof plan === 'object' ? (plan as InstructorPlan) : null;
  });

  /** Commission rate as a display percentage string, e.g. "20%". */
  feePercent = computed<string>(() => {
    const rate = this.activePlan()?.platformFeeRate;
    if (rate == null) return '—';
    return `${Math.round(rate * 100)}%`;
  });

  loading = signal(false);
  withdrawalError = signal<string | null>(null);
  withdrawalSuccess = signal(false);
  showWithdrawalForm = signal(false);
  showBankAccountForm = signal(false);

  readonly MIN_WITHDRAWAL = 5000;

  /** Resolved public URL for the bank book image, if one is stored. */
  bankBookImageUrl = computed<string | null>(() => {
    const account = this.bankAccount();
    if (!account?.bankBookResource) return null;
    const res = account.bankBookResource;
    if (typeof res === 'string') return null; // raw ID without populated path
    return res.path ?? null;
  });

  /** Whether the stored bank book is an image (can be previewed inline). */
  bankBookIsImage = computed<boolean>(() => {
    const account = this.bankAccount();
    if (
      !account?.bankBookResource ||
      typeof account.bankBookResource === 'string'
    )
      return false;
    const res = account.bankBookResource;
    // Resource type 'image' from ResourceType enum or infer from file extension
    if (res.type === 'image') return true;
    const name = res.name ?? res.path ?? '';
    return /\.(jpe?g|png|gif|webp)$/i.test(name);
  });

  /** Popup state for image preview */
  showBankBookPreview = signal(false);

  openBankBookPreview(): void {
    this.showBankBookPreview.set(true);
  }

  closeBankBookPreview(): void {
    this.showBankBookPreview.set(false);
  }

  ngOnInit(): void {
    this.subscription.add(
      this.tenantService.currentTenant$.subscribe((tenant) => {
        this.currentTenant.set(tenant);
      })
    );
    this.subscription.add(
      this.store.select(selectUser).subscribe((u) => {
        this.user.set(u);
      })
    );
    this.loadData();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  loadData(): void {
    this.loading.set(true);

    forkJoin({
      ledger: this.earningService.getLedger(),
      earningsRes: this.earningService.getAll(1, 10),
      withdrawalsRes: this.withdrawalService.getOwn(1, 10),
      bankAccount: this.bankAccountService.get().pipe(catchError(() => of(null))),
      planAssignment: this.planService.getMyPlan().pipe(catchError(() => of(null))),
    })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: ({ ledger, earningsRes, withdrawalsRes, bankAccount, planAssignment }) => {
          this.ledger.set(ledger);
          this.earnings.set(earningsRes.docs);
          this.totalEarningsCount.set(earningsRes.totalDocs);
          this.withdrawals.set(withdrawalsRes.docs);
          this.totalWithdrawalsCount.set(withdrawalsRes.totalDocs);
          this.bankAccount.set(bankAccount);
          this.activePlanAssignment.set(planAssignment);
        },
        error: () => {
          // Individual partial failures leave signals in their current state;
          // loading is always cleared by finalize() above.
        },
      });
  }

  openWithdrawalForm(): void {
    this.withdrawalError.set(null);
    this.withdrawalSuccess.set(false);
    this.showWithdrawalForm.set(true);
  }

  closeWithdrawalForm(): void {
    this.showWithdrawalForm.set(false);
  }

  submitWithdrawal(): void {
    const available = this.ledger()?.availableForWithdrawal ?? 0;
    if (available < this.MIN_WITHDRAWAL) {
      this.withdrawalError.set(
        `Minimum withdrawal is LKR ${this.MIN_WITHDRAWAL}`
      );
      return;
    }

    if (!this.bankAccount()?._id) {
      this.withdrawalError.set('Please save a bank account first');
      return;
    }

    this.withdrawalService.requestWithdrawal(available).subscribe({
      next: () => {
        this.withdrawalSuccess.set(true);
        this.showWithdrawalForm.set(false);
        this.loadData();
      },
      error: (err) => {
        this.withdrawalError.set(
          err?.error?.message ?? 'Withdrawal request failed'
        );
      },
    });
  }

  openBankAccountForm(): void {
    this.showBankAccountForm.set(true);
  }

  closeBankAccountForm(): void {
    this.showBankAccountForm.set(false);
  }

  saveBankAccount(data: Omit<BankAccount, '_id' | 'user'>): void {
    this.bankAccountService.save(data).subscribe({
      next: (account) => {
        this.bankAccount.set(account);
        this.showBankAccountForm.set(false);
      },
    });
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'PENDING_GRACE':
        return 'daisy-badge-warning';
      case 'PROCESSING':
        return 'daisy-badge-warning';
      case 'AVAILABLE':
        return 'daisy-badge-success';
      case 'WITHDRAWN':
        return 'daisy-badge-neutral';
      case 'SETTLED_DIRECTLY':
        return 'daisy-badge-info';
      default:
        return 'daisy-badge-ghost';
    }
  }

  getWithdrawalStatusClass(status: string): string {
    switch (status) {
      case 'REQUESTED':
        return 'daisy-badge-info';
      case 'PROCESSING':
        return 'daisy-badge-warning';
      case 'COMPLETED':
        return 'daisy-badge-success';
      case 'REJECTED':
        return 'daisy-badge-error';
      default:
        return 'daisy-badge-ghost';
    }
  }
}
